import { session } from '@/utils/storage'

/**
 * Remembers the last query string each dashboard page was visited with, so that
 * navigating "back" to a list through a breadcrumb (which only knows the static
 * path) lands on the same filtered/sorted/paginated view the user left.
 *
 * Browser Back already restores the params — every list keeps its state in the
 * URL — but breadcrumb links are plain hrefs, so without this they reset the
 * list to its unfiltered first page.
 */

const STORAGE_KEY = 'route-params-memory'

/** pathname -> search string (including the leading `?`) */
type RouteParamsMemory = Record<string, string>

/**
 * Detail pages are keyed by their own pathname (`/billing/cachier/123`), so the
 * map would grow with every visited record. Keep only the most recent entries.
 */
const MAX_ENTRIES = 50

let cache: RouteParamsMemory | null = null

const getMemory = (): RouteParamsMemory => {
  if (cache === null) cache = session.loadState(STORAGE_KEY) ?? {}
  return cache as RouteParamsMemory
}

export const rememberRouteParams = (pathname: string, search: string) => {
  const memory = getMemory()

  // Re-insert so the key moves to the end: object key order is the eviction
  // order below.
  delete memory[pathname]
  if (search) memory[pathname] = search

  const keys = Object.keys(memory)
  for (const key of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) {
    delete memory[key]
  }

  session.saveState(STORAGE_KEY, memory)
}

export const getRememberedRouteParams = (pathname: string): string =>
  getMemory()[pathname] ?? ''

/**
 * Appends the params `pathname` was last visited with to a static link.
 * Links that already carry their own query/hash are returned untouched — an
 * explicitly built target always wins over the remembered one.
 */
export const withRememberedRouteParams = (href: string): string => {
  if (!href.startsWith('/') || href.includes('?') || href.includes('#')) {
    return href
  }
  return `${href}${getRememberedRouteParams(href)}`
}

export const clearRouteParamsMemory = () => {
  cache = {}
  session.saveState(STORAGE_KEY, {})
}
